import { useState } from 'react';
import { type Brand, brandLinks } from '../lib/brand';

// A highlighted "brand" pill (🏷️ <name>) shown on the mentor card + profile. Clicking it
// opens a popup with the brand's logo, tagline, website and social links. Rendered as a
// <span role=button> (not <button>) so it can sit inside the card's <a> link without
// invalid nesting; the click is stopped from triggering the card navigation.
export function BrandPill({ brand, small = false, label = '🏷️', wide = false }: { brand: Brand; small?: boolean; label?: string; wide?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setOpen(true); } }}
        title={`${brand.name} — website & social links`}
        style={{
          // `wide` fills the parent (e.g. a 75%-of-card wrapper) with a larger font, but
          // stays a SINGLE line (ellipsis on overflow) so a long brand name can't grow the
          // card height / spill outside it. Otherwise it's a content-width inline pill.
          ...(wide
            ? { display: 'block', width: '100%', boxSizing: 'border-box' as const, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, textAlign: 'center' as const }
            : { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem', whiteSpace: 'nowrap' as const }),
          padding: wide ? '0.4rem 0.9rem' : (small ? '0.13rem 0.55rem' : '0.25rem 0.7rem'),
          borderRadius: 999,
          border: '1px solid #f59e0b', background: 'linear-gradient(135deg,#fffbeb,#fde68a)',
          color: '#92400e', fontWeight: wide ? 800 : 700,
          fontSize: wide ? '0.95rem' : (small ? '0.72rem' : '0.8rem'),
          cursor: 'pointer', lineHeight: 1.3,
        }}
      >
        {label ? `${label} ` : ''}{brand.name}
      </span>
      {open && <BrandCard brand={brand} onClose={() => setOpen(false)} />}
    </>
  );
}

function BrandCard({ brand, onClose }: { brand: Brand; onClose: () => void }) {
  const links = brandLinks(brand);
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000 }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', width: '100%', maxWidth: 420, background: 'white', borderRadius: 14, padding: '1.4rem', boxShadow: '0 12px 40px rgba(0,0,0,0.25)', fontFamily: 'system-ui, sans-serif' }}>
        <button type="button" onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: 10, right: 12, border: 'none', background: 'none', fontSize: '1.2rem', color: '#64748b', cursor: 'pointer' }}>✕</button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
          {brand.logoUrl
            ? <img src={brand.logoUrl} alt={brand.name} width={52} height={52} style={{ borderRadius: 10, objectFit: 'cover', border: '1px solid #eee' }} />
            : <span style={{ width: 52, height: 52, borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#fef3c7', fontSize: '1.4rem' }}>🏷️</span>}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#92400e' }}>{brand.name}</div>
            {brand.tagline && <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.1rem' }}>{brand.tagline}</div>}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1.1rem' }}>
          {links.length === 0
            ? <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: 0 }}>No links added yet.</p>
            : links.map((l) => (
              <a key={l.key} href={l.href} target="_blank" rel="noopener nofollow noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0.75rem', borderRadius: 9, border: '1px solid #e5e7eb', background: '#fafafa', textDecoration: 'none', color: '#1f2937' }}>
                <span style={{ fontSize: '1rem' }}>{l.icon}</span>
                <span style={{ fontWeight: 600 }}>{l.label}</span>
                <span style={{ marginLeft: 'auto', color: '#6b7280', fontSize: '0.76rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 170 }}>{l.href.replace(/^https?:\/\//, '')}</span>
              </a>
            ))}
        </div>
      </div>
    </div>
  );
}
